/**
 * Painel "Mapa": liga/desliga as camadas vetoriais, agrupadas por tema, e
 * ajusta o estilo de cada uma. Aparência geral, clique no mapa e fontes ficam
 * no diálogo de configurações (engrenagem), como na aba Satélite.
 */
import { Fragment, useEffect, useId, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  BookOpen,
  ChevronDown,
  Info,
  Layers,
  LoaderCircle,
  Palette,
  RotateCcw,
  Settings2,
  SlidersHorizontal,
  TriangleAlert,
} from 'lucide-react';
import {
  Badge,
  Button,
  ColorInput,
  Dialog,
  Hint,
  IconButton,
  Section,
  Segmented,
  Slider,
  Toggle,
  type SelectOption,
} from '../ui/primitives';
import { formatBytes } from '../lib/format';
import { readSectionOpen, writeSectionOpen } from '../ui/sectionState';
import {
  LAYER_GROUPS,
  PRESETS,
  STYLE_FIELDS,
  VECTOR_LAYERS,
  getPreset,
  type LayerGroupDef,
  type StyleControl,
  type VectorLayerDef,
} from './catalog';
import { ensureManifest, retryFailed } from './loader';
import { selectLayerBytes, selectLayerStatus, useVectorStore } from './store';
import { resolveStyle } from './styleConfig';
import type { LayerStyle, PresetId, StyleKey } from './types';

const PRESET_OPTIONS: SelectOption<PresetId>[] = PRESETS.map((p) => ({ value: p.id, label: p.label }));

const DENSITY_OPTIONS: SelectOption<'-1' | '0' | '1'>[] = [
  { value: '-1', label: 'Menos' },
  { value: '0', label: 'Normal' },
  { value: '1', label: 'Mais' },
];

function formatNumber(key: StyleKey, v: number): string {
  if (key === 'opacity' || key === 'fillOpacity') return `${Math.round(v * 100)}%`;
  if (key === 'strokeWidth') return `${v.toFixed(1).replace('.', ',')} px`;
  return `${v} px`;
}

// ---------------------------------------------------------------------------
// Controles de estilo
// ---------------------------------------------------------------------------

function StyleControlInput(props: {
  control: StyleControl;
  style: LayerStyle;
  disabled: boolean;
  onChange: (patch: Partial<LayerStyle>) => void;
}) {
  const { control, style, disabled, onChange } = props;
  const { key, label } = control;
  const spec = STYLE_FIELDS[key];
  const value = style[key];

  if (spec.kind === 'boolean') {
    return <Toggle size="sm" checked={Boolean(value)} onChange={(v) => onChange({ [key]: v })} label={label} disabled={disabled} />;
  }
  if (spec.kind === 'color') {
    return (
      <div className={disabled ? 'pointer-events-none opacity-50' : undefined} aria-disabled={disabled}>
        <ColorInput label={label} value={String(value)} onChange={(hex) => onChange({ [key]: hex })} />
      </div>
    );
  }
  if (key === 'density') {
    return (
      <div className={disabled ? 'pointer-events-none opacity-50' : undefined} aria-disabled={disabled}>
        <Segmented
          label={label}
          size="sm"
          value={String(Math.max(-1, Math.min(1, Number(value)))) as '-1' | '0' | '1'}
          options={DENSITY_OPTIONS}
          onChange={(v) => onChange({ density: Number(v) })}
        />
      </div>
    );
  }
  return (
    <Slider
      label={label}
      value={Number(value)}
      min={spec.min}
      max={spec.max}
      step={spec.step}
      disabled={disabled}
      format={(v) => formatNumber(key, v)}
      onChange={(v) => onChange({ [key]: v })}
    />
  );
}

function StyleEditor(props: { def: VectorLayerDef }) {
  const { def } = props;
  const preset = useVectorStore((s) => s.preset);
  const override = useVectorStore((s) => s.overrides[def.id]);
  const setStyle = useVectorStore((s) => s.setStyle);
  const resetStyle = useVectorStore((s) => s.resetStyle);
  const style = useMemo(() => resolveStyle(def.id, preset, override), [def.id, preset, override]);
  const bytes = useVectorStore((s) => selectLayerBytes(s, def.id));

  return (
    <div className="mt-3 space-y-3 rounded-lg border border-ink-700 bg-ink-850/80 p-3">
      <Hint>
        {def.description}
        {bytes !== null && ` Arquivo: ${formatBytes(bytes)}.`}
      </Hint>
      {def.controls.map((c) => (
        <StyleControlInput
          key={c.key}
          control={c}
          style={style}
          disabled={c.dependsOn ? !style[c.dependsOn] : false}
          onChange={(patch) => setStyle(def.id, patch)}
        />
      ))}
      <div className="flex justify-end">
        <Button size="sm" variant="ghost" icon={RotateCcw} disabled={!override} onClick={() => resetStyle(def.id)}>
          Restaurar padrão
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Linha de camada
// ---------------------------------------------------------------------------

function Legend(props: { def: VectorLayerDef }) {
  const { def } = props;
  const preset = useVectorStore((s) => s.preset);
  const override = useVectorStore((s) => s.overrides[def.id]);
  const style = useMemo(() => resolveStyle(def.id, preset, override), [def.id, preset, override]);
  if (!def.legend) return null;
  return (
    <ul className="mt-2 ml-12 space-y-1.5">
      {def.legend.map((item) => (
        <li key={item.label} className="flex items-start gap-2 text-xs">
          <span
            className="mt-0.5 size-3 shrink-0 rounded-sm border"
            style={{ borderColor: String(style[item.colorKey]), background: `${String(style[item.colorKey])}55` }}
            aria-hidden
          />
          <span>
            <span className="text-ink-100">{item.label}</span>
            {item.hint && <span className="block leading-snug text-ink-400">{item.hint}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

function LayerStatus(props: { def: VectorLayerDef }) {
  const { def } = props;
  const status = useVectorStore((s) => selectLayerStatus(s, def.id));
  const bytes = useVectorStore((s) => selectLayerBytes(s, def.id));
  const size = bytes !== null ? formatBytes(bytes) : null;

  if (status === 'loading') {
    return (
      <span className="mt-1 ml-12 flex items-center gap-1 text-[11px] text-ink-300" role="status">
        <LoaderCircle className="size-3 animate-spin" aria-hidden />
        Carregando{size ? ` (${size})` : ''}…
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="mt-1 ml-12 flex items-center gap-1 text-[11px] text-yellow-300">
        <TriangleAlert className="size-3" aria-hidden />
        Não foi possível carregar.
      </span>
    );
  }
  if (status === 'missing') {
    return <span className="mt-1 ml-12 block text-[11px] text-ink-300">Indisponível no momento.</span>;
  }
  return null;
}

function LayerRow(props: { def: VectorLayerDef }) {
  const { def } = props;
  const visible = useVectorStore((s) => s.visible[def.id]);
  const status = useVectorStore((s) => selectLayerStatus(s, def.id));
  const customized = useVectorStore((s) => Boolean(s.overrides[def.id]));
  const setVisible = useVectorStore((s) => s.setVisible);
  const [open, setOpen] = useState(false);

  return (
    <div data-vector-layer={def.id}>
      <Toggle
        checked={visible && status !== 'missing'}
        onChange={(v) => {
          if (v && status === 'error') retryFailed(def.data);
          setVisible(def.id, v);
        }}
        disabled={status === 'missing'}
        label={
          <span className="flex items-center gap-1.5" title={def.description}>
            {def.title}
            {customized && <Badge tone="mata">ajustado</Badge>}
          </span>
        }
        right={
          <IconButton
            icon={SlidersHorizontal}
            label={`Ajustar aparência: ${def.title}`}
            size="sm"
            active={open}
            onClick={() => setOpen((o) => !o)}
          />
        }
      />
      <LayerStatus def={def} />
      {def.legend && visible && <Legend def={def} />}
      {open && <StyleEditor def={def} />}
    </div>
  );
}

/** Grupo recolhível de camadas (lembra se o usuário abriu ou fechou). */
function LayerGroup(props: { group: LayerGroupDef }) {
  const { group } = props;
  const defs = useMemo(() => VECTOR_LAYERS.filter((d) => d.group === group.id), [group.id]);
  const active = useVectorStore((s) => defs.filter((d) => s.visible[d.id]).length);
  const storageKey = `mapa:${group.id}`;
  const [open, setOpenState] = useState(() => readSectionOpen(storageKey) ?? group.defaultOpen);
  const setOpen = (next: boolean) => {
    setOpenState(next);
    writeSectionOpen(storageKey, next);
  };
  const id = useId();

  return (
    <div className="border-t border-ink-700/50 pt-2 first:border-t-0 first:pt-0">
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded-md py-1.5 text-left text-xs font-medium text-ink-300 hover:text-ink-100"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen(!open)}
      >
        <ChevronDown className={clsx('size-3.5 shrink-0 transition-transform', !open && '-rotate-90')} aria-hidden />
        <span className="flex-1">{group.title}</span>
        {active > 0 && (
          <span className="rounded-full bg-mata-500/20 px-1.5 py-px text-[10px] font-medium tracking-normal text-mata-300 normal-case">
            {active === 1 ? '1 ligada' : `${active} ligadas`}
          </span>
        )}
      </button>
      {open && (
        <div id={id} className="space-y-3.5 pt-1.5 pb-2">
          {group.hint && <Hint>{group.hint}</Hint>}
          {defs.map((def) => (
            <LayerRow key={def.id} def={def} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Créditos
// ---------------------------------------------------------------------------

function Credits() {
  const manifest = useVectorStore((s) => s.manifest);
  const sources = useMemo(() => {
    if (!manifest) return [];
    const seen = new Map<string, { source: string; url?: string; license?: string }>();
    for (const l of Object.values(manifest.layers)) {
      if (!l?.source || seen.has(l.source)) continue;
      seen.set(l.source, { source: l.source, url: l.sourceUrl, license: l.license });
    }
    return [...seen.values()];
  }, [manifest]);
  if (sources.length === 0) return null;
  return (
    <div>
      <Hint>
        {sources.map((s, i) => (
          <Fragment key={s.source}>
            {i > 0 && ' · '}
            {s.url ? (
              <a
                href={s.url}
                target="_blank"
                rel="noreferrer"
                title={s.license ? `Licença: ${s.license}` : undefined}
                className="underline decoration-ink-500 underline-offset-2 hover:text-ink-200"
              >
                {s.source}
              </a>
            ) : (
              <span title={s.license ? `Licença: ${s.license}` : undefined}>{s.source}</span>
            )}
          </Fragment>
        ))}
      </Hint>
    </div>
  );
}

/**
 * Aviso único de falha de carregamento, com um só "Tentar de novo" para
 * todas as camadas com erro. (O carregador já repete sozinho com espera
 * crescente e tenta de novo quando a conexão volta.)
 */
function LoadErrorNotice() {
  const manifestFailed = useVectorStore((s) => s.manifestState.status === 'error');
  const failedLayers = useVectorStore((s) => VECTOR_LAYERS.filter((d) => selectLayerStatus(s, d.id) === 'error').length);
  if (!manifestFailed && failedLayers === 0) return null;
  const what =
    failedLayers === 0
      ? 'Não foi possível carregar a lista de camadas.'
      : failedLayers === 1
        ? 'Uma camada não carregou.'
        : `${failedLayers} camadas não carregaram.`;
  return (
    <div className="flex items-start gap-2 rounded-lg border border-yellow-400/30 bg-yellow-400/10 p-2.5 text-xs text-yellow-200" role="alert">
      <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
      <span className="flex-1 leading-snug">
        {what} Tentaremos de novo quando a conexão voltar.
      </span>
      <Button size="sm" onClick={() => retryFailed()}>
        Tentar de novo
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Painel
// ---------------------------------------------------------------------------

/** Configurações do mapa: aparência geral, clique no mapa e fontes dos dados. */
function MapSettingsDialog(props: { open: boolean; onClose: () => void }) {
  const preset = useVectorStore((s) => s.preset);
  const hasOverrides = useVectorStore((s) => Object.keys(s.overrides).length > 0);
  const applyPreset = useVectorStore((s) => s.applyPreset);
  const highlight = useVectorStore((s) => s.highlight);
  const setHighlight = useVectorStore((s) => s.setHighlight);

  return (
    <Dialog open={props.open} onClose={props.onClose} title="Configurações do mapa">
      <div className="space-y-6">
        <section className="space-y-3" aria-labelledby="cfg-aparencia">
          <h3 id="cfg-aparencia" className="flex items-center gap-2 text-sm font-semibold text-white">
            <Palette className="size-4 text-mata-400" aria-hidden />
            Aparência do mapa
          </h3>
          <Segmented value={preset} options={PRESET_OPTIONS} onChange={applyPreset} />
          <Hint>
            {getPreset(preset).description}
            {hasOverrides && ' Trocar o estilo desfaz os ajustes feitos nas camadas.'}
          </Hint>
        </section>
        <section className="space-y-3 border-t border-ink-700/70 pt-5" aria-labelledby="cfg-clique">
          <h3 id="cfg-clique" className="flex items-center gap-2 text-sm font-semibold text-white">
            <Info className="size-4 text-mata-400" aria-hidden />
            Ao clicar no mapa
          </h3>
          <Hint>
            Clique no mapa para ver o nome do município, da sub-região, da terra indígena, da unidade de conservação, do
            rio ou da rodovia.
          </Hint>
          <Toggle
            checked={highlight}
            onChange={setHighlight}
            label="Destacar a área clicada"
            description="Contorna em amarelo a área escolhida enquanto o quadro de informações estiver aberto."
          />
        </section>
        <section className="space-y-3 border-t border-ink-700/70 pt-5" aria-labelledby="cfg-fontes">
          <h3 id="cfg-fontes" className="flex items-center gap-2 text-sm font-semibold text-white">
            <BookOpen className="size-4 text-mata-400" aria-hidden />
            Fontes dos dados
          </h3>
          <Credits />
        </section>
      </div>
    </Dialog>
  );
}

export function VectorPanel() {
  const active = useVectorStore((s) => VECTOR_LAYERS.filter((d) => s.visible[d.id]).length);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    void ensureManifest().catch(() => {
      // O erro fica no store e é mostrado abaixo.
    });
  }, []);

  return (
    <div>
      <Section
        title="Camadas do mapa"
        icon={Layers}
        right={
          <div className="flex items-center gap-1">
            <Badge>{`${active}/${VECTOR_LAYERS.length}`}</Badge>
            <IconButton
              icon={Settings2}
              size="sm"
              label="Configurações do mapa (aparência, clique e fontes)"
              onClick={() => setSettingsOpen(true)}
              className="pointer-coarse:size-11"
            />
          </div>
        }
      >
        <LoadErrorNotice />
        <div className="space-y-1">
          {LAYER_GROUPS.map((g) => (
            <LayerGroup key={g.id} group={g} />
          ))}
        </div>
      </Section>
      <MapSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
}

export default VectorPanel;
